/** The frame the error pages share: a marshal's flag, a number, and a way out.
 *
 * Every page in the set is the same column of text — a flag swatch beside a
 * kicker, the status at 120px, one headline, one paragraph, two buttons and a
 * monospace line for whoever has to debug it — next to an illustration that
 * belongs to that flag alone. The column is here; the illustrations live with
 * the page that owns them.
 *
 * The Worker draws the same designs for a browser that navigates to an API
 * path (`src/lib/errorPage.ts`), and the two are kept word-for-word alike.
 * This one exists because the Worker never sees a bad portal address: the
 * assets binding answers every unknown path with `index.html` and a `200`,
 * and it is the router in here that finds out nothing matches.
 *
 * Under 800px the illustration goes and the column stands alone. It is
 * decoration — `aria-hidden`, and nothing in it is said only there.
 */

import type { ReactNode } from 'react';
import { Kicker } from '@/components/ui/Kicker';

export interface FlagPageProps {
  status: number;
  /** One of the `.flag-swatch-*` classes in `tokens.css`. */
  swatch: string;
  kicker: string;
  title: string;
  /** The paragraph under the headline. */
  children: ReactNode;
  /** Two buttons: a primary and a secondary. */
  actions: ReactNode;
  /** The monospace line: what was asked for, or who was asking. */
  diag?: ReactNode;
  /** The illustration, or the 500's field of red. */
  art: ReactNode;
  /** `wide` gives the text the larger share and runs the art to the edges —
   *  the red flag only, whose right-hand column is a field rather than a
   *  drawing. */
  wide?: boolean;
  /** A soft accent glow behind the stage. */
  glow?: string;
}

export function FlagPage({
  status,
  swatch,
  kicker,
  title,
  children,
  actions,
  diag,
  art,
  wide,
  glow,
}: FlagPageProps) {
  const columns = wide
    ? 'min-[801px]:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]'
    : 'min-[801px]:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]';

  return (
    <main
      aria-label={`${status} — ${kicker}`}
      className={`grid grid-cols-1 ${columns}`}
      style={{
        flex: 1,
        position: 'relative',
        alignItems: wide ? 'stretch' : 'center',
        gap: wide ? 0 : 40,
        padding: wide ? 0 : '32px 20px 40px',
      }}
    >
      {glow ? (
        <div
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: glow }}
        />
      ) : null}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 20,
          position: 'relative',
          minWidth: 0,
          padding: wide ? '32px 20px 40px' : 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className={`flag-swatch ${swatch}`} aria-hidden="true" />
          <Kicker className="flag-kicker">{kicker}</Kicker>
        </div>

        <p
          className="tabular-nums"
          style={{
            margin: 0,
            fontSize: 'clamp(84px, 22vw, 120px)',
            lineHeight: 0.9,
            letterSpacing: '-0.04em',
            color: 'var(--color-neutral-200)',
          }}
        >
          {status}
        </p>

        <h1 style={{ fontSize: 32, margin: 0, lineHeight: 1.1, textWrap: 'balance' }}>{title}</h1>

        <p
          style={{
            margin: 0,
            color: 'var(--color-neutral-400)',
            fontSize: 15,
            maxWidth: wide ? 460 : 420,
            textWrap: 'pretty',
          }}
        >
          {children}
        </p>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>{actions}</div>

        {diag ? (
          <p
            style={{
              margin: 0,
              fontSize: 12,
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-neutral-600)',
              overflowWrap: 'anywhere',
            }}
          >
            {diag}
          </p>
        ) : null}
      </div>

      <div aria-hidden="true" className="max-[800px]:hidden" style={{ position: 'relative', minWidth: 0 }}>
        {art}
      </div>
    </main>
  );
}

export default FlagPage;
